import { useNotificationServiceNotificationItemsRetrieve } from '@notification/api/queries'
import { renderTemplateAsText } from '@notification/utils'
import { getFormatCurrency, getFullName } from '@shared/utils'
import { Card, Descriptions, DescriptionsProps, Flex } from 'antd'
import { useTranslation } from 'react-i18next'

import { NotificationTemplatePreview } from '../NotificationTemplatePreview'

type Props = {
  id: number
}

const htmlTagRegex = /<[a-z][\s\S]*>/i

const renderMessage = (message: string) => {
  if (!message) return '-'

  if (htmlTagRegex.test(message))
    return (
      <div
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: message }}
      />
    )

  const urlRegex = /(https?:\/\/[^\s]+)/g

  const parts = message.split(urlRegex)

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a key={index} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      )
    }
    return part
  })
}

export const NotificationDetails = ({ id }: Props) => {
  const { t } = useTranslation()

  const { data: notification, isLoading } =
    useNotificationServiceNotificationItemsRetrieve({ id })

  const items: DescriptionsProps['items'] = [
    {
      label: t('product'),
      children:
        notification?.context?.order?.product?.name ||
        notification?.context?.product?.name
    },
    {
      label: t('status'),
      children: notification?.context?.order?.status
        ? t(`statuses.${notification?.context?.order?.status}`)
        : null
    },
    {
      label: t('price'),
      children: notification?.context?.order?.amount
        ? getFormatCurrency({
            amount: notification?.context?.order?.amount
          })
        : null
    },
    {
      label: t('user'),
      children: notification?.context?.order?.user
        ? getFullName(notification?.context?.order?.user)
        : notification?.context?.customer?.full_name
    },
    {
      label: t('table.subject'),
      style: { minWidth: 140 },
      children: renderTemplateAsText(
        notification?.message_subject,
        notification?.context
      )
    },
    {
      label: t('email'),
      children:
        notification?.context?.order?.user?.email ||
        notification?.context?.customer?.email
    },
    {
      label: t('table.details'),
      children: (
        <Flex gap={8}>
          <NotificationTemplatePreview
            value={notification?.message_body}
            context={notification?.context}
          />
          {renderMessage(
            renderTemplateAsText(
              notification?.message_body,
              notification?.context
            )
          )}
        </Flex>
      )
    }
  ].filter(({ children }) => !!children)

  return (
    <Card loading={isLoading}>
      <Descriptions bordered items={items} column={2} />
    </Card>
  )
}
